import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { contactDetails } from "@/mocks/products";

const milestones = [
  {
    year: "1956",
    title: "Gründung",
    text: "Franz Drexelius gründet in Finnentrop-Heggen eine Werkstatt für Metallverarbeitung.",
  },
  {
    year: "1970er",
    title: "Spezialisierung",
    text: "Der Betrieb konzentriert sich auf Drückerverbindungen und Stifte für die Beschlagtechnik.",
  },
  {
    year: "1990er",
    title: "Serienfertigung",
    text: "Ausbau der Dreh- und Frästechnik für Fertigungslose ab 300 Stück.",
  },
  {
    year: "Heute",
    title: "Spezialfabrik",
    text: "Sechs Stiftfamilien, Sonderanfertigungen nach Muster und persönliche Beratung.",
  },
];

const values = [
  {
    icon: "ri-focus-3-line",
    title: "Präzision",
    text: "Enge Toleranzen und gleichbleibende Maßhaltigkeit über die gesamte Serie.",
  },
  {
    icon: "ri-hand-heart-line",
    title: "Handwerk",
    text: "Über 65 Jahre Erfahrung in der Zerspanung von Stahl und Edelstahl.",
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Standort Deutschland",
    text: "Fertigung, Beratung und Versand aus Finnentrop-Heggen in Nordrhein-Westfalen.",
  },
  {
    icon: "ri-lightbulb-line",
    title: "Flexibilität",
    text: "Sonderausführungen nach Zeichnung oder Muster gehören bei uns zum Alltag.",
  },
];

function UnternehmenPage() {
  return (
    <SiteLayout>
      <div className="w-full bg-background-100 border-b border-background-200">
        <nav aria-label="Brotkrumen" className="w-full px-4 md:px-8 py-4">
          <ol className="flex flex-wrap items-center gap-2 font-label text-xs text-foreground-500">
            <li>
              <Link to="/" className="hover:text-primary-600 transition-colors">
                Start
              </Link>
            </li>
            <li aria-hidden="true">
              <i className="ri-arrow-right-s-line" />
            </li>
            <li className="text-foreground-800">Unternehmen</li>
          </ol>
        </nav>
      </div>

      <section className="w-full bg-foreground-950 text-background-50">
        <div className="w-full px-4 md:px-8 py-14 md:py-20 max-w-4xl">
          <p className="font-label text-[11px] uppercase tracking-[0.18em] text-accent-300">
            Unternehmen
          </p>
          <h1 className="mt-4 font-heading font-semibold text-3xl md:text-5xl leading-[1.1]">
            Ein Familienbetrieb mit Spezialisierung statt Massenware
          </h1>
          <p className="mt-6 text-base md:text-lg text-background-200 leading-relaxed">
            Wir sind die Spezialisten für Türdrücker-Stifte, Wechsel-Schlossstifte, PSW-Stifte und
            Vierkant-Stifte. Seit 1956 fertigen wir in Finnentrop-Heggen Verbindungselemente, die in
            der Tür- und Beschlagtechnik täglich im Einsatz sind.
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          <div>
            <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground-950">
              Von der Werkstatt zur Spezialfabrik
            </h2>
            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              Was 1956 als kleine Metallwerkstatt begann, ist heute ein spezialisierter
              Fertigungsbetrieb für Drückerverbindungen. Wir liefern an Beschlaghersteller, den
              Fachgroßhandel und Handwerksbetriebe – national und im europäischen Ausland.
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              Weil wir uns bewusst auf ein schmales Produktspektrum konzentrieren, kennen wir jedes
              Teil im Detail. Das zahlt sich für unsere Kunden aus: kurze Wege, klare Auskünfte und
              die Bereitschaft, auch außergewöhnliche Anforderungen zu fertigen.
            </p>
            <div className="mt-9 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {values.map((value) => (
                <article
                  key={value.title}
                  className="rounded-lg bg-background-100 border border-background-200 p-5"
                >
                  <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-900 flex items-center justify-center">
                    <i className={`${value.icon} text-lg`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-base text-foreground-950">
                    {value.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{value.text}</p>
                </article>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-semibold text-2xl md:text-3xl text-foreground-950">
              Meilensteine
            </h2>
            <ol className="mt-6 relative border-l border-background-300 pl-6 flex flex-col gap-8">
              {milestones.map((item) => (
                <li key={item.year} className="relative">
                  <span
                    className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-accent-500 ring-4 ring-background-50"
                    aria-hidden="true"
                  />
                  <p className="font-label text-[11px] uppercase tracking-wide text-primary-600">
                    {item.year}
                  </p>
                  <h3 className="mt-1 font-heading font-semibold text-lg text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{item.text}</p>
                </li>
              ))}
            </ol>

            <div className="mt-10 rounded-lg bg-primary-800 text-background-50 p-6 md:p-8">
              <h3 className="font-heading font-semibold text-lg">Direkter Draht zur Werkstatt</h3>
              <p className="mt-3 text-sm text-background-100/90 leading-relaxed">
                Telefonisch erreichen Sie uns während der Geschäftszeiten. Für Anfragen mit Muster
                oder Zeichnung nutzen Sie am besten unser Kontaktformular.
              </p>
              <div className="mt-5 flex flex-col gap-2.5 text-sm">
                <a href={contactDetails.phoneHref} className="flex items-center gap-2 hover:text-accent-300 transition-colors">
                  <i className="ri-phone-line text-accent-300" aria-hidden="true" />
                  Telefon {contactDetails.phone}
                </a>
                <span className="flex items-center gap-2">
                  <i className="ri-printer-line text-accent-300" aria-hidden="true" />
                  Telefax {contactDetails.fax}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}

export default UnternehmenPage;